'use strict';

export function isString(value: any): boolean {
  return typeof value === 'string';
}

export function isFunction(value: any): boolean {
  return typeof value === 'function';
}

export function isNumber(value: any): boolean {
  return typeof value === 'number';
}

export function isInteger(value: any): boolean {
  return (value | 0) === value;
}

export function isArray(value: any): boolean {
  return Object.prototype.toString.call(value) === '[object Array]';
}

export function toNumber(x: any): number {
  return +x;
}

export function toArray(arr: any): Array<any> {
  return [].slice.call(arr);
}

export function toPixels(x: any): string {
  return x + 'px';
}

export function occurrences(string: string, substring: string): number {
  if (substring.length <= 0)
    return string.length + 1;

  let n: number = 0;
  let pos: number = 0;
  let step: number = substring.length;

  while (true) {
    pos = string.indexOf(substring, pos);
    if (pos >= 0) {
      n++;
      pos += step;
    } else {
      break;
    }
  }

  return n;
}

export function sum(array: number[]): number {
  return array.reduce((sum: number, x: number) => sum + x, 0);
}

export function range(start: number, end: number, step: number = 1): number[] {
  if (step === 0) { throw new Error('Step can never be 0.'); }
  let length: number = Math.max(Math.ceil((end - start) / step), 0);
  let range: number[] = Array(length);
  for (let i: number = 0; i < length; i++) {
    range[i] = start;
    start += step;
  }
  return range;
}

export function find<T>(array: T[], predicate: (item: T) => boolean): T | null {
  let length: number = array.length;
  for (let index: number = 0; index < length; index++) {
    if (predicate(array[index])) {
      return array[index];
    }
  }
  return null;
}

export function uniq<T>(array: T[], eq?: (a: T, b: T) => boolean): T[] {
  eq = (eq === undefined ? ((a: T, b: T) => a === b) : eq);
  return array.reduce((acc: T[], x: T) => {
    if (find<T>(acc, (y: T) => eq!(x, y)) === null) {
      acc.push(x);
    }
    return acc;
  }, []);
}

/**
 * Compare two ordered sequences, returning the indexes where
 * their values are deemed unequal by the provided function, or,
 * if `eq` is not provided, by the built-in strict equality.
 */
export function diff<T>(xs: ArrayLike<T>, ys: ArrayLike<T>,
                        eq?: (a: T, b: T) => boolean): number[] {
  eq = (eq === undefined ? ((a: T, b: T) => a === b) : eq);

  if (xs.length !== ys.length) {
    throw TypeError('Sequences of different length');
  }

  let indexes: number[] = [];
  for (let i: number = 0; i < xs.length; i++) {
    if (!eq(xs[i], ys[i])) {
      indexes.push(i);
    }
  }

  return indexes;
}

export function flatMap<T, U>(array: T[], f: (x: T) => U[]): U[] {
  return array.reduce((acc: U[], x: T) => acc.concat(f(x)), []);
}

export function html(tag: string, classes?: string[]): HTMLElement {
  let element: HTMLElement = document.createElement(tag);
  if (classes !== undefined) {
    classes.forEach((c: string) => { element.classList.add(c); });
  }
  return element;
}

export function removeClasses(el: HTMLElement, selector: string,
                              classes: string[]): void {
  toArray(el.querySelectorAll(selector)).forEach((e: HTMLElement) => {
    e.classList.remove(...classes);
  });
}

export function isChildOf(child: HTMLElement, parent: HTMLElement): boolean {
  let element: HTMLElement;

  element = child;
  while (element) {
    if (element === parent) {
      return true;
    } else if (element.parentElement === null) {
      return false;
    }

    element = element.parentElement;
  }
  return false;
}

export function clickInElement(click: MouseEvent, el: HTMLElement): boolean {
  const rect: ClientRect = el.getBoundingClientRect();
  return !(click.clientX < rect.left
      || click.clientX > rect.left + rect.width
      || click.clientY < rect.top
      || click.clientY > rect.top + rect.height);
}

export function elementInViewport(el: HTMLElement) {
  const rect: ClientRect = el.getBoundingClientRect();
  return rect.top >= 0
      && rect.left >= 0
      && rect.bottom <= (window.innerHeight || document.documentElement.clientHeight)
      && rect.right <= (window.innerWidth || document.documentElement.clientWidth);
}

export function clientHasLocalStorage(): boolean {
  try {
    const x: string = '__storage_test__';
    window['localStorage'].setItem(x, x);
    window['localStorage'].removeItem(x);
    return true;
  } catch (e) {
    return false;
  }
}

export interface Direction extends Array<number> { }

export class Direction {
  static None: Direction = [0, 0];
  static Vertical: Direction = [1, 0];
  static Horizontal: Direction = [0, 1];
  static VerticalReverse: Direction = [-1, 0];
  static HorizontalReverse: Direction = [0, -1];

  static equal([a1, a2]: Direction, [b1, b2]: Direction): boolean {
    return a1 === b1 && a2 === b2;
  }

  static aligns([a1, a2]: Direction, [b1, b2]: Direction): boolean {
    return Math.abs(a1) === Math.abs(b1) && Math.abs(a2) === Math.abs(b2);
  }

  static reverse([a, b]: Direction): Direction {
    return [a * -1, b * -1];
  }

  static isReverse([a, b]: Direction): boolean {
    return a === -1 || b === -1;
  }

  static swap([a, b]: Direction): Direction {
    return [b, a];
  }
}

// TODO: Refactor. Index could be removed entirely.

export interface Position extends Array<number> { }

export module Index {
  export interface t { readonly value: number; readonly width: number; }

  export function create(index: number, width: number): t {
    return { 'value': index, 'width': width };
  }

  export function equal(a: t, b: t): boolean {
    return a.value === b.value;
  }

  export function from2D(row: number, col: number, width: number): t {
    return create(row * width + col, width);
  }

  export function to2D(i: Index.t): Position {
    return [Math.floor(i.value / i.width), i.value % i.width];
  }

  export function toString(index: number, width: number): string {
    return `${Math.floor(index / width)}:${index % width}`;
  }

  /**
   * Return the next index from the passed one according to the
   * given row and column modifiers. Any change to columns is
   * prevented from wrapping. Generated indexes are not checked
   * for validity and might be out of bounds.
   */
  export function next(index: t, row: number, col: number): t | null {
    let value: number;
    let a: number;
    let b: number;

    if (row === 0 && col === 0 || row !== 0 && col !== 0) {
      throw TypeError('Invalid modifiers.');
    }

    value = index.value + row * index.width + col;

    // Check for wrapping
    if (col !== 0) {
      a = Math.floor(index.value / index.width);
      b = Math.floor(value / index.width);
      if (a !== b) {
        return null; // row has changed
      }
    }

    return create(value, index.width);
  }
}
